/**
 * BLRRD HDR Pipeline — public surface for Phase 2A.
 *
 *   mergeBrackets(blobs)        — JPEG/PNG blobs → fused ACEScg Float32
 *   fuseBrackets(stack)         — merge N pixel-aligned exposure brackets (Float32)
 *   fuseBracketsCPU(stack)      — Uint8/Float32-tolerant CPU adapter (Lane D entry)
 *   fuseBracketsGPU(stack)      — WebGL2 GPU path; throws if no GPU available
 *   fuseBracketsAuto(stack)     — picks GPU when present, falls back to CPU
 *   selectBackend()             — returns { backend, instance } for the active path
 *   gpuAvailable                — boolean snapshot — true iff a GPU backend was probed OK
 *   estimateEVStops(brackets)   — masked-median log-luminance EV estimate
 *   parseExifEV(blob)           — read absolute EV from EXIF (piexifjs)
 *   synthesizeBrackets(raw)     — render N EV-stops from a single linear RAW
 *   rawToHDR(bytes)             — single RAW → fused HDR via re-develop brackets
 *   reinhard / hable / acesFitted — display tone-mappers
 *
 * The functions are pure and deliberately decoupled — callers build the
 * pipeline they need (decode → bracket → fuse → tone-map → encode) without
 * HDR knowing about the editor's render kernel.
 *
 * The output of `mergeBrackets` / `fuseBrackets` is **linear ACEScg** with
 * synthetic alpha = 1.0. To inject into the editor, run it through
 * `ingestLinearACEScg` from `src/pipeline/ingest.js` (passthrough — no
 * matrix re-application).
 */

import { fuseBrackets } from './mertens.js';
import { ingestSRGB }   from '../ingest.js';
import {
    selectBackend as selectBackendImpl,
    fuseBracketsAuto, fuseBracketsGPU, gpuAvailable as gpuAvailableFn,
    disposeBackend,
} from './backend.js';
import { WebGL2Backend } from './gpu/webgl2.js';

export { fuseBrackets, computeWeights, normalizeWeights,
         gaussianPyramid, laplacianPyramid, collapsePyramid,
         buildLaplacianWeight, buildStdevWeight,
         DEFAULT_MU_EV, DEFAULT_SIGMA_EV, DEFAULT_EV_FLOOR } from './mertens.js';
export { guidedFilter, boxFilter, luminanceFromRGBA, selectGuideIndex,
         defaultRadius as defaultGuidedRadius,
         DEFAULT_GUIDED_EPS } from './guided-filter.js';
/* Phase 2.A.2 — AI-Robust HDR Fusion. */
export { pixelMedianMaps, pixelMedianRGBA,
         hybridMix, mixMapFromWeights,
         applyHybridBlendInPlace } from './median-stack.js';
export { detectFusionMode, AUTO_DETECT_THRESHOLDS,
         pearson as autoDetectPearson,
         luminanceMap as autoDetectLuminance,
         block4xMean as autoDetectBlock4xMean } from './auto-detect.js';
export { decodeRaw, synthesizeBrackets, applyEV, rawToHDR }   from './raw-brackets.js';
export { reinhard, hable, acesFitted }                         from './tone-map.js';
export { mergeBrackets }                                       from './merge.js';
export { estimateEVStops, parseExifEV }                        from './exposure.js';
export { WebGL2Backend, fuseBracketsAuto, fuseBracketsGPU, disposeBackend };
export const selectBackend = selectBackendImpl;
export const gpuAvailable  = gpuAvailableFn;

/**
 * Forgiving CPU adapter — accepts brackets in either of two shapes:
 *
 *   { rgba: Float32Array, width, height }  — ACEScg-linear, fed straight in
 *   { data: Uint8ClampedArray, width, height } — sRGB-encoded, ingested first
 *
 * Mixed inputs are allowed. Returns the same `{ rgba, width, height }` shape
 * as `fuseBrackets`. Used by tests + the eventual UI wrapper that holds raw
 * ImageData. New file allocations only — never mutates inputs.
 *
 * @param {Array<{rgba?:Float32Array,data?:Uint8ClampedArray|Uint8Array,width:number,height:number}>} stack
 * @param {object} [opts]   forwarded to fuseBrackets (levels, signal, …)
 * @returns {{ rgba: Float32Array, width: number, height: number }}
 */
export function fuseBracketsCPU(stack, opts = {}) {
    if (!Array.isArray(stack) || stack.length < 2) {
        throw new Error('fuseBracketsCPU: need at least 2 brackets');
    }
    const f32Stack = stack.map((b, i) => {
        if (!b || !b.width || !b.height) {
            throw new Error(`fuseBracketsCPU: bracket[${i}] missing width/height`);
        }
        if (b.rgba instanceof Float32Array) {
            return { rgba: b.rgba, width: b.width, height: b.height };
        }
        if (b.data instanceof Uint8ClampedArray || b.data instanceof Uint8Array) {
            const rgba = ingestSRGB(b.data, b.width, b.height);
            return { rgba, width: b.width, height: b.height };
        }
        throw new Error(`fuseBracketsCPU: bracket[${i}] needs .rgba (Float32) or .data (Uint8)`);
    });
    return fuseBrackets(f32Stack, opts);
}
